import * as Sentry from '@sentry/react'
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig } from 'axios'

import { getImpersonation } from '@/lib/impersonation'
import { getAuthSession, getAuthSessionSnapshot, logout, refreshAuthSession } from '@/utils/auth'
import { clearAuthInterruption, setAuthInterruption } from '@/utils/authInterruption'

/**
 * In Vite dev, remote APIs (e.g. api-east) do not send CORS for localhost. Keep
 * `VITE_BACKEND_URL` absolute for OAuth redirects (Login), but use same-origin
 * requests here so `/api/*` is forwarded by the dev server proxy.
 */
function resolveApiBaseURL(): string {
  const configured = import.meta.env.VITE_BACKEND_URL ?? ''
  if (!import.meta.env.DEV) {
    return configured || 'https://localhost:8000'
  }
  if (!configured) {
    return ''
  }
  try {
    const hostname = new URL(configured).hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return configured
    }
  } catch {
    return ''
  }
  return ''
}

// Create axios instance with default config
const client = axios.create({
  baseURL: resolveApiBaseURL(),
  timeout: 20000,
})

// Types for API responses
export interface ApiResponse<T> {
  data: T
  status: number
  attributionRevision?: string
}

export interface FetchAPIConfig extends AxiosRequestConfig {
  /**
   * Statuses the caller resolves as part of a normal flow, so they are not reported as
   * request failures. For a user-driven confirmation the report is noise, not a signal.
   */
  expectedStatuses?: number[]
}

export interface ApiError {
  message: string
  status: number
  detail?: string | null
  /** No HTTP response at all — offline, DNS/TLS, or an egress/DLP proxy. Not expressible as a 5xx. */
  isNetworkError?: boolean
  /** Machine-readable code from a structured `detail` object, when the route sends one. */
  code?: string
  /** The structured `detail` object verbatim, for routes carrying extra fields (e.g. `conflicts`). */
  detailData?: Record<string, unknown>
  /** The session changed while this request was in flight; its result must not be applied. */
  isAuthContextChanged?: boolean
}

/** Names the two things a user can actually check, unlike "please try again". */
export const NETWORK_FAILURE_MESSAGE = "Couldn't reach Wispr Flow — check your connection or VPN."

const ENTERPRISE_IP_NOT_ALLOWED_CODE = 'enterprise_ip_not_allowed'
const IP_ALLOWLIST_VIOLATION_TEXT_PATTERNS = [
  /\byour ip address is not allowed\b/,
  /\bip address is not allowed\b/,
  /\benterprise[_\s-]?ip[_\s-]?not[_\s-]?allowed\b/,
]
let isLoggingOutAfterIpViolation = false
let isRecoveringDeadSession = false
let lastDeadSessionRecoveryAt = 0
/** Bounds refresh-token rotation when a 401 keeps arriving from something other than a dead session. */
const DEAD_SESSION_RECOVERY_COOLDOWN_MS = 5000

interface AuthIdentity {
  provider: string
  userId: string
  email: string
}

class AuthContextChangedError extends Error {}

const getAuthIdentity = (session: Awaited<ReturnType<typeof getAuthSession>>): AuthIdentity | null =>
  session
    ? {
        provider: session.provider,
        userId: session.user.id ?? session.user.providerUserId ?? session.user.email,
        email: session.user.email,
      }
    : null

const sameAuthIdentity = (left: AuthIdentity | null, right: AuthIdentity | null): boolean =>
  left?.provider === right?.provider && left?.userId === right?.userId && left?.email === right?.email

const authContextChangedError = (): ApiError => ({
  message: 'Authentication changed while the request was in progress',
  status: 499,
  isAuthContextChanged: true,
})

const hasAuthContextChanged = async (identity: AuthIdentity | null): Promise<boolean> => {
  try {
    return !sameAuthIdentity(identity, getAuthIdentity(await getAuthSessionSnapshot()))
  } catch {
    return true
  }
}

/** `/email/create-user` is an authenticated @wispr.ai-only admin route, unlike the rest of `/email/*`. */
const AUTHENTICATED_EMAIL_ROUTES = ['/email/create-user']

function routePath(url: string | undefined): string {
  return (url ?? '').split('?')[0]
}

const DYNAMIC_SEGMENT_PATTERNS = [
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^\d+$/,
  /@|%40/,
  /^(?=.*\d)[A-Za-z0-9_.%~-]{16,}$/,
]

/**
 * Route template for Sentry tags: `/members/<uuid>` → `/members/{id}`. Resolved paths carry
 * enterprise/user ids and, on `/invitations/<email>`, an address — indexed PII and unbounded cardinality.
 */
function routeTemplate(url: string | undefined): string {
  return routePath(url)
    .split('/')
    .map((segment) => (DYNAMIC_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment)) ? '{id}' : segment))
    .join('/')
}

/** Pre-session login routes: a 401 here is a real login failure, not an expired token. */
function isLoginRoute(url: string | undefined): boolean {
  const path = routePath(url)
  return path === '/user_status' || path.startsWith('/email/')
}

/** A login route that runs before any session exists, so a 401 there is a credentials failure. */
function isPreSessionRoute(url: string | undefined): boolean {
  return isLoginRoute(url) && !AUTHENTICATED_EMAIL_ROUTES.includes(routePath(url))
}

function shouldAttachSession(url: string | undefined): boolean {
  return !isPreSessionRoute(url)
}

function captureRequestFailure(error: AxiosError, tags: Record<string, string>): void {
  if (error.config) {
    // Axios serializes config into the Sentry payload: the body carries the password, the headers a
    // live access token, and the query string the user's email.
    error.config = {
      ...error.config,
      url: routeTemplate(error.config.url),
      data: undefined,
      headers: new AxiosHeaders(),
    }
  }
  Sentry.captureException(error, { tags })
}

interface ParsedErrorBody {
  detail?: string
  message?: string
  slowapiError?: string
  code?: string
  detailData?: Record<string, unknown>
}

function parseErrorBody(body: unknown): ParsedErrorBody {
  if (!body || typeof body !== 'object') {
    return {}
  }

  const bodyRecord = body as Record<string, unknown>
  const detailField = bodyRecord.detail

  let detail: string | undefined
  let code: string | undefined
  let detailData: Record<string, unknown> | undefined

  if (typeof detailField === 'string') {
    detail = detailField
  } else if (detailField && typeof detailField === 'object' && !Array.isArray(detailField)) {
    // A FastAPI 422 sends `detail` as a list, and `typeof [] === 'object'` — assigning
    // that to `detailData` would type an array as a record.
    const detailRecord = detailField as Record<string, unknown>
    detailData = detailRecord
    if (typeof detailRecord.message === 'string') {
      detail = detailRecord.message
    }
    if (typeof detailRecord.code === 'string') {
      code = detailRecord.code
    }
  }

  // Typed GT 400/409 responses use flat {reason, ...} bodies.
  if (detailData === undefined && typeof bodyRecord.reason === 'string') {
    detailData = bodyRecord
  }

  const message = typeof bodyRecord.message === 'string' ? bodyRecord.message : undefined
  const slowapiError = typeof bodyRecord.error === 'string' ? bodyRecord.error : undefined
  const topLevelCode = typeof bodyRecord.code === 'string' ? bodyRecord.code : undefined

  return { detail, message, slowapiError, code: code ?? topLevelCode, detailData }
}

function isIpAllowlistViolation(status: number, parsed: ParsedErrorBody): boolean {
  if (status !== 403) {
    return false
  }
  if (parsed.code?.toLowerCase() === ENTERPRISE_IP_NOT_ALLOWED_CODE) {
    return true
  }
  const textCandidates = [parsed.detail, parsed.message, parsed.slowapiError]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase().replace(/\s+/g, ' ').trim())

  return textCandidates.some((text) => IP_ALLOWLIST_VIOLATION_TEXT_PATTERNS.some((pattern) => pattern.test(text)))
}

async function handleIpAllowlistViolationLogout(config: AxiosRequestConfig): Promise<void> {
  setAuthInterruption('ip_allowlist_violation')

  if (isLoggingOutAfterIpViolation) {
    return
  }

  isLoggingOutAfterIpViolation = true
  try {
    await logout()
  } catch (logoutError) {
    Sentry.captureException(logoutError, {
      tags: {
        api_url: routeTemplate(config.url) || 'unknown',
        api_method: config.method || 'unknown',
        reason: 'ip_allowlist_logout_failed',
      },
    })
  } finally {
    isLoggingOutAfterIpViolation = false
  }
}

/**
 * A 401 on an authenticated route means the backend rejected the token we sent. Try one provider
 * refresh, and sign out only when that provider confirms the session is gone.
 */
async function handleDeadSessionRecovery(config: AxiosRequestConfig): Promise<void> {
  if (isRecoveringDeadSession || Date.now() - lastDeadSessionRecoveryAt < DEAD_SESSION_RECOVERY_COOLDOWN_MS) {
    return
  }

  isRecoveringDeadSession = true
  // Set before the await: a definitive refresh failure drops the session itself, and the resulting
  // navigation to /login reads this synchronously.
  setAuthInterruption('session_expired')
  try {
    if (await sessionSurvivedRefresh()) {
      clearAuthInterruption()
      return
    }
    await logout()
  } catch (logoutError) {
    Sentry.captureException(logoutError, {
      tags: {
        api_url: routeTemplate(config.url) || 'unknown',
        api_method: config.method || 'unknown',
        reason: 'dead_session_logout_failed',
      },
    })
  } finally {
    lastDeadSessionRecoveryAt = Date.now()
    isRecoveringDeadSession = false
  }
}

/**
 * Only a definitive provider rejection proves the session is gone. Network failures and provider
 * 5xx responses must leave the user signed in because logout clears persisted app state.
 */
async function sessionSurvivedRefresh(): Promise<boolean> {
  try {
    return (await refreshAuthSession()).survived
  } catch {
    return true
  }
}

/**
 * Generic API request helper
 */
export async function fetchAPI<T>(fetchConfig: FetchAPIConfig): Promise<ApiResponse<T>> {
  const { expectedStatuses, ...config } = fetchConfig
  let sentWithSession = false
  let authIdentity: AuthIdentity | null = null
  let hasCapturedAuthIdentity = false
  try {
    if (shouldAttachSession(config.url)) {
      const session = await getAuthSession()
      authIdentity = getAuthIdentity(session)
      hasCapturedAuthIdentity = true
      const accessToken =
        session && (session.expiresAt === null || session.expiresAt > Date.now() / 1000)
          ? session.accessToken
          : undefined

      // Add auth header if token provided
      if (accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: accessToken,
        }
        sentWithSession = true
      }
    }

    // Add impersonation header when active
    const impersonating = getImpersonation()
    if (impersonating) {
      config.headers = {
        ...config.headers,
        'X-Impersonate-Enterprise': impersonating.enterpriseId,
      }
    }

    // Handle URL prefixing
    if (!config.url) {
      config.url = '/api/v1/enterprise'
    } else if (config.url === '') {
      config.url = '/api/v1/enterprise'
    } else if (
      !config.url.startsWith('/api/v1') &&
      !config.url.startsWith('/user_status') &&
      !config.url.startsWith('/email') &&
      !config.url.startsWith('/history')
    ) {
      const adjustedUrl = config.url.startsWith('/') ? config.url : '/' + config.url
      config.url = `/api/v1/enterprise${adjustedUrl}`
    }

    const response = await client(config)
    if (hasCapturedAuthIdentity && (await hasAuthContextChanged(authIdentity))) {
      throw new AuthContextChangedError()
    }
    const revision = Object.entries(response.headers ?? {}).find(
      ([name]) => name.toLowerCase() === 'x-attribution-revision'
    )?.[1]

    return {
      data: response.data,
      status: response.status,
      ...(typeof revision === 'string' && /^[a-f0-9]{64}$/.test(revision) ? { attributionRevision: revision } : {}),
    }
  } catch (error) {
    if (error instanceof AuthContextChangedError) throw authContextChangedError()

    // Handle Axios errors
    if (error instanceof AxiosError) {
      if (hasCapturedAuthIdentity && (await hasAuthContextChanged(authIdentity))) {
        throw authContextChangedError()
      }
      // A cancel also has no `response`, and it must not read as unreachable-server.
      const isCancelled = error.code === AxiosError.ERR_CANCELED
      const isNetworkError = error.response === undefined && !isCancelled
      const status = error.response?.status || 500
      const body = error.response?.data
      const parsedErrorBody = parseErrorBody(body)
      const hasIpAllowlistViolation = isIpAllowlistViolation(status, parsedErrorBody)
      const isUnauthorized = status === 401
      // Without a token of our own on the request, a 401 says nothing about our session.
      const hasDeadSession = isUnauthorized && sentWithSession

      if (hasIpAllowlistViolation) {
        await handleIpAllowlistViolationLogout(config)
      } else if (hasDeadSession) {
        await handleDeadSessionRecovery(config)
      }

      // A superseded request is not a failure. Debounced search and paging abort the previous
      // one by design, so reporting these buries real errors under cancellations.
      const isExpected = expectedStatuses?.includes(status) ?? false
      if (
        (!isUnauthorized || isPreSessionRoute(config.url)) &&
        !hasIpAllowlistViolation &&
        !isCancelled &&
        !isExpected
      ) {
        captureRequestFailure(error, {
          api_url: routeTemplate(config.url) || 'unknown',
          api_method: config.method || 'unknown',
          status_code: String(error.response?.status),
        })
      }
      const friendly = isNetworkError
        ? NETWORK_FAILURE_MESSAGE
        : status === 429
          ? 'Too many requests. Please wait a moment and try again.'
          : parsedErrorBody.detail || parsedErrorBody.message || parsedErrorBody.slowapiError || error.message
      throw {
        message: friendly,
        status,
        detail: parsedErrorBody.detail || parsedErrorBody.slowapiError,
        isNetworkError,
        code: parsedErrorBody.code,
        detailData: parsedErrorBody.detailData,
      } as ApiError
    }
    // Handle other errors
    Sentry.captureException(error, {
      tags: { api_url: routeTemplate(config.url) || 'unknown' },
    })
    throw {
      message: 'An unexpected error occurred',
      status: 500,
    } as ApiError
  }
}

/** Typeguard for ApiError */
export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'message' in error && 'status' in error
}
